import React, { useMemo } from 'react';
import { Box, BoxExtendedProps, Inscription, Text, useResponsive } from '@common-fe/common-fe';

interface Props extends BoxExtendedProps {
  title: string;
  description: string | React.ReactNode;

  icon: React.ReactNode;
  testId?: string;
  children?: React.ReactNode;
  isWideMode?: boolean;
  isNew?: boolean;
}

const Banner: React.FC<Props> = ({
  isNew,
  title,
  description,
  icon,
  testId,
  children,
  isWideMode,
  ...rest
}) => {
  const { isMobile } = useResponsive();
  const sizeOfImage = useMemo(() => {
    if (isMobile) {
      return '130px';
    }
    return '200px';
  }, [isMobile]);
  return (
    <Box
      width={isWideMode || isMobile ? undefined : '984px'}
      pad={
        isMobile
          ? { left: 'spacing24', right: 'spacing24', bottom: 'spacing24', top: 'spacing8' }
          : { left: 'spacing24', right: 'spacing72', vertical: 'spacing24' }
      }
      direction={isMobile ? 'column-reverse' : 'row'}
      height={isMobile ? (children ? '400px' : undefined) : '248px'}
      round="container1Round"
      background="canvas"
      border={
        isNew
          ? {
              color: 'success',
              size: 'large',
            }
          : { color: 'border2', size: 'small' }
      }
      align="center"
      justify={isMobile ? 'end' : 'start'}
      data-testId={`${testId}_banner`}
      {...rest}
    >
      <Box
        fill={isMobile}
        justify={isMobile ? 'between' : undefined}
        direction={isMobile ? 'column' : undefined}
        width={{ max: isWideMode || isMobile ? undefined : '665px', width: '100%' }}
      >
        {isNew ? (
          <Box style={{ position: 'absolute', top: '0', right: '0' }}>
            <Inscription
              margin={{ top: '12px', right: '12px' }}
              color="success"
              lineHeight="20px"
              weight="bold"
            >
              New
            </Inscription>
          </Box>
        ) : null}
        <Box>
          <Box data-testId={`${testId}_banner-title`} margin={isMobile ? { top: 'spacing12' } : {}}>
            <Text weight={700} size="2xl" truncate={isMobile}>
              {title}
            </Text>
          </Box>

          <Box data-testId={`${testId}_banner-subtitle`} margin={{ top: 'xs' }}>
            {typeof description === 'string' ? <Text size="medium">{description}</Text> : description}
          </Box>
        </Box>

        {children}
      </Box>

      <Box
        data-testId={`${testId}_banner-icon-wrapper`}
        margin={isMobile ? undefined : { left: 'spacing24' }}
        justify="center"
        align="center"
        width={{ width: sizeOfImage, min: sizeOfImage }}
        height={{ height: sizeOfImage, min: sizeOfImage }}
      >
        {icon}
      </Box>
    </Box>
  );
};

export default Banner;
